import React from "react";
import {
  FaCamera,
  FaEnvelope,
  FaHome,
  FaIdCard,
  FaPen,
  FaPhoneAlt,
  FaTimes,
  FaUser,
} from "react-icons/fa";

import {
  formatDate,
  getGroupColorClass,
  getInitials,
} from "../utils/inscriptionsUtils";

function DetailItem({ label, value, icon: Icon }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-start gap-3">
        {Icon && (
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-white text-slate-500 dark:bg-slate-950 dark:text-slate-300">
            <Icon />
          </span>
        )}

        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
            {label}
          </p>
          <p className="mt-1 break-words text-sm font-semibold text-slate-800 dark:text-slate-100">
            {value || "—"}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function AdherentDetails({
  adherent,
  groupColorMap,
  onEdit,
  onClose,
}) {
  return (
    <div className="space-y-6">
      <section className="rounded-[2rem] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="grid h-20 w-20 shrink-0 place-items-center rounded-3xl bg-gradient-to-br from-orange-100 to-blue-100 text-2xl font-bold text-slate-800 dark:from-orange-500/20 dark:to-blue-500/20 dark:text-white">
            {getInitials(adherent)}
          </div>

          <div className="min-w-0 flex-1">
            <h3 className="text-2xl font-bold text-slate-950 dark:text-white">
              {adherent.prenom} {adherent.nom}
            </h3>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span
                className={`rounded-full px-3 py-1 text-xs font-bold ${getGroupColorClass(
                  groupColorMap,
                  adherent.adherent_groupe
                )}`}
              >
                {adherent.adherent_groupe || "Sans groupe"}
              </span>

              <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {adherent.genre === "F" ? "Féminin" : "Masculin"}
              </span>

              <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                Né(e) le {formatDate(adherent.date_naissance)}
              </span>
            </div>
          </div>
        </div>
      </section>

      <section>
        <h4 className="mb-3 text-sm font-bold uppercase tracking-[0.18em] text-slate-400">
          Coordonnées
        </h4>

        <div className="grid gap-3 sm:grid-cols-2">
          <DetailItem label="Email" value={adherent.email} icon={FaEnvelope} />
          <DetailItem
            label="Email secondaire"
            value={adherent.email_secondaire}
            icon={FaEnvelope}
          />
          <DetailItem label="Téléphone" value={adherent.tel} icon={FaPhoneAlt} />
          <DetailItem
            label="Téléphone secondaire"
            value={adherent.tel_secondaire}
            icon={FaPhoneAlt}
          />
          <div className="sm:col-span-2">
            <DetailItem
              label="Adresse"
              value={[adherent.adresse, adherent.cp, adherent.commune]
                .filter(Boolean)
                .join(" ")}
              icon={FaHome}
            />
          </div>
        </div>
      </section>

      <section>
        <h4 className="mb-3 text-sm font-bold uppercase tracking-[0.18em] text-slate-400">
          Informations sportives
        </h4>

        <div className="grid gap-3 sm:grid-cols-2">
          <DetailItem
            label="N° de licence"
            value={adherent.licence_number || "Non renseigné"}
            icon={FaIdCard}
          />
          <DetailItem
            label="Poids"
            value={adherent.poids ? `${adherent.poids} kg` : "Non renseigné"}
            icon={FaUser}
          />
          <DetailItem
            label="Autorisation photo"
            value={adherent.autorisation_photo ? "Oui" : "Non"}
            icon={FaCamera}
          />
          <DetailItem
            label="Créé le"
            value={formatDate(adherent.createdAt || adherent.created_at)}
            icon={FaUser}
          />
        </div>
      </section>

      <section>
        <h4 className="mb-3 text-sm font-bold uppercase tracking-[0.18em] text-slate-400">
          Notes
        </h4>

        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
          <p className="whitespace-pre-line text-sm leading-7 text-slate-700 dark:text-slate-300">
            {adherent.notes_adherent || "Aucune note renseignée."}
          </p>
        </div>
      </section>

      <div className="sticky bottom-0 -mx-5 border-t border-slate-200 bg-white/90 px-5 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Fermer
          </button>

          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-slate-950/20 transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
          >
            <FaPen />
            Modifier
          </button>
        </div>
      </div>
    </div>
  );
}